import * as i0 from '@angular/core';
import { EventEmitter, Component, Input, Output, Injectable, Inject, ViewChild, forwardRef, Optional, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { startWith, map, Subject, takeUntil } from 'rxjs';
import * as i2 from '@engineering11/ui-lib/e11-input';
import { E11InputModule } from '@engineering11/ui-lib/e11-input';
import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2$1 from '@engineering11/ui-lib/e11-button';
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button';
import * as i6 from '@engineering11/web-utilities';
import { WebUtilityModule } from '@engineering11/web-utilities';
import * as i4 from '@engineering11/angular-instantsearch';
import { TypedBaseWidget, NgAisIndex, NgAisInstantSearch, NgAisModule } from '@engineering11/angular-instantsearch';
import { takeUntil as takeUntil$1 } from 'rxjs/operators';
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import { sort, compareKeyDesc, sleep } from '@engineering11/utility';
import * as i1$2 from '@engineering11/web-api-error';
import { LOGGER_TOKEN } from '@engineering11/web-api-error';
import { CollectionRepository, FieldValue, FirestoreQueryBuilder } from '@engineering11/web-api-firebase';
import connectSortBy from 'instantsearch.js/es/connectors/sort-by/connectSortBy';
import * as i3 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import MiniSearch from 'minisearch';

/**
 * A simple wrapper around a search input that subscribes to form changes and emits them as they come
 * Simple usage: <search-form (inputChanged)="searchQuery$.next($event)"></search-form>
 * where searchQuery$ is a BehaviourSubject
 */
class SearchFormComponent {
    constructor(formBuilder) {
        this.formBuilder = formBuilder;
        this.label = '';
        this.placeholder = 'Search';
        this.searchForm = this.formBuilder.group({
            query: new FormControl(''),
        });
        this.searchQuery$ = this.searchForm.valueChanges.pipe(startWith(undefined), map((changes) => changes?.query || undefined));
        this.inputChanged = new EventEmitter();
        this.destroy$ = new Subject();
    }
    ngOnInit() {
        this.searchQuery$.pipe(takeUntil(this.destroy$)).subscribe((query) => this.inputChanged.emit(query));
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SearchFormComponent, deps: [{ token: i1.FormBuilder }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SearchFormComponent, selector: "sdk-search-form", inputs: { label: "label", placeholder: "placeholder" }, outputs: { inputChanged: "inputChanged" }, ngImport: i0, template: `
    <form [formGroup]="searchForm">
      <e11-input [type]="'search'" [label]="label" [placeholder]="placeholder" [parentForm]="searchForm" formControlName="query"></e11-input>
    </form>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: i2.E11InputComponent, selector: "e11-input", inputs: ["containerClass", "inputClass", "hintText", "label", "type", "size", "style", "placeholder", "maxLength", "max", "name", "id", "iconLeft", "iconLeftBorder", "required", "hasError", "labelColor", "borderGlow", "checkIcon", "clearIcon", "autofocus", "togglePassword", "hasButton", "buttonText", "buttonIcon", "buttonIconType", "buttonIconSize", "readonly", "disabled", "disabledText", "autocomplete", "formControlName", "isRounded", "parentForm", "tooltipConfig", "icon", "iconType", "iconSize", "darkMode", "context", "enforceCharacterLimit", "showCharacterLimitWarning", "loading", "leadingIcon", "trailingIcon"], outputs: ["change", "focus", "blur"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SearchFormComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sdk-search-form',
                    template: `
    <form [formGroup]="searchForm">
      <e11-input [type]="'search'" [label]="label" [placeholder]="placeholder" [parentForm]="searchForm" formControlName="query"></e11-input>
    </form>
  `,
                }]
        }], ctorParameters: () => [{ type: i1.FormBuilder }], propDecorators: { label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], inputChanged: [{
                type: Output
            }] } });

const COLLECTION_AUTOCOMPLETE = 'search_autocomplete/';
class AutoCompleteRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = COLLECTION_AUTOCOMPLETE;
    }
    /**
     * Increments the counter on the autocomplete document by 1 so we know its relative usage
     * @param id
     */
    increment(model) {
        return this.upsert({ ...model, useCount: FieldValue.increment(1) });
    }
    /**
     * Useful for autocomplete on a small number of records and filtering in-memory.
     * Scope is required since it is expected that is only be used for records created by a single customer
     * Rule of thumb: firestore fetch and in-memory search is cheaper than Algolia up to ~10k search records
     */
    getAllByTypeAndScope(type, scope) {
        const queryBuilder = new FirestoreQueryBuilder().where('type', '==', type).where('scope', '==', scope);
        return this.query(queryBuilder.build());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutoCompleteRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutoCompleteRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutoCompleteRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class AutocompleteService {
    constructor(repository, logger) {
        this.repository = repository;
        this.logger = logger;
    }
    increment(model) {
        const modelToUpsert = { ...model, id: this.buildId(model) };
        this.logger.log('AutocompleteService increment', modelToUpsert);
        return this.repository.increment(modelToUpsert);
    }
    /**
     * Useful for autocomplete on a small number of records (<10k) and filtering in-memory.
     * Scope is required since it is expected that is only be used for records created by a single customer
     * Rule of thumb: firestore fetch and in-memory search is cheaper than Algolia up to ~10k search records
     */
    getAllByTypeAndScope(type, scope) {
        return this.repository.getAllByTypeAndScope(type, scope).pipe(map((autocompleteArray) => sort(autocompleteArray, compareKeyDesc('useCount'))));
    }
    buildId(model) {
        if (model.scope) {
            return `${model.scope}_${model.type}_${model.value}`;
        }
        return `${model.type}_${model.value}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutocompleteService, deps: [{ token: AutoCompleteRepository }, { token: LOGGER_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutocompleteService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutocompleteService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: AutoCompleteRepository }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGGER_TOKEN]
                }] }] });

class AutocompleteComponent {
    constructor(autocompleteService, elementRef, formBuilder) {
        this.autocompleteService = autocompleteService;
        this.formBuilder = formBuilder;
        this.valueSelected = new EventEmitter();
        this.dropdownHidden = true;
        this.clickedInside = false;
        this.formAutocomplete = new FormGroup({}); // ! Do NOT type new forms as "any" - this is from the Angular 14 migration
        this.searchCurrent = '';
        this.destroy$ = new Subject();
        this.elementRef = elementRef;
    }
    ngOnInit() {
        this.typesenseAdapter = new TypesenseInstantSearchAdapter({
            server: this.configuration,
            // The following parameters are directly passed to Typesense's search API endpoint.
            //  So you can pass any parameters supported by the search endpoint below.
            //  query_by is required.
            additionalSearchParameters: {
                query_by: 'value',
            },
        });
        this.config = {
            indexName: 'autocomplete',
            searchClient: this.typesenseAdapter.searchClient,
            insightsClient: window.aa,
        };
        this.initForm();
    }
    ngAfterViewInit() {
        if (this.searchCurrent) {
            this.searchByInput(this.searchCurrent);
        }
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
    openDropdown() {
        this.dropdownHidden = false;
    }
    async onSelect(event) {
        const value = event;
        this.chooseValue(value);
    }
    get f() {
        return this.formAutocomplete.controls;
    }
    onSubmitForm() {
        if (this.formAutocomplete.valid) {
            const value = this.f.value.value;
            this.chooseValue(value);
            this.formAutocomplete.controls['value'].setValue('');
        }
    }
    initForm() {
        this.formAutocomplete = this.formBuilder.group({
            value: new FormControl(),
        });
        this.formAutocomplete.valueChanges.pipe(takeUntil$1(this.destroy$)).subscribe((changes) => {
            this.searchCurrent = this.formAutocomplete.value.value;
            this.searchByInput(this.searchCurrent);
        });
    }
    chooseValue(value) {
        this.valueSelected.emit(value);
        this.dropdownHidden = true;
        return this.autocompleteService.increment({ type: this.type, value });
    }
    searchByInput(searchString) {
        this.instantSearchInstance?.instantSearchInstance?.helper?.setQuery(searchString)?.search();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutocompleteComponent, deps: [{ token: AutocompleteService }, { token: i0.ElementRef }, { token: i1.FormBuilder }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: AutocompleteComponent, selector: "sdk-search-autocomplete", inputs: { type: "type", configuration: "configuration" }, outputs: { valueSelected: "valueSelected" }, viewQueries: [{ propertyName: "instantSearchInstance", first: true, predicate: ["instantSearch"], descendants: true }], ngImport: i0, template: "<ais-instantsearch *ngIf=\"config\" #instantSearch [config]=\"config\">\n  <form class=\"e11-relative\" [formGroup]=\"formAutocomplete\" (ngSubmit)=\"onSubmitForm()\" novalidate>\n    <span #dropdownInput>\n      <e11-input\n        [type]=\"'text'\"\n        name=\"value\"\n        id=\"id_title\"\n        [borderGlow]=\"'primary'\"\n        [parentForm]=\"formAutocomplete\"\n        formControlName=\"value\"\n        autocomplete=\"off\"\n        (click)=\"openDropdown()\"\n        [label]=\"'Skills'\"\n      >\n      </e11-input>\n    </span>\n    <ais-hits>\n      <ng-template #hits let-hits=\"hits\" let-results=\"results\">\n        <div\n          [dropdownCloser]=\"dropdownInput\"\n          (closeEventTriggered)=\"dropdownHidden = true\"\n          class=\"suggestions-container e11-z-10 e11-absolute e11-bg-white e11-h-auto e11-shadow-md e11-box-border\"\n          [ngClass]=\"{ 'is-hidden': dropdownHidden }\"\n        >\n          <ul class=\"e11-p-0 e11-m-0 e11-max-h-60 e11-overflow-y-auto\">\n            <li class=\"e11-relative e11-list-none e11-p-0 e11-m-0 e11-cursor-pointer\" *ngFor=\"let hit of hits\">\n              <a\n                class=\"e11-py-3.5 e11-px-4 e11-block e11-no-underlin e11-text-skin-base e11-cursor-pointer hover:e11-bg-gray-200\"\n                [class.complete-selected]\n                (click)=\"onSelect(hit.value)\"\n                >{{ hit.value }}</a\n              >\n            </li>\n          </ul>\n        </div>\n      </ng-template>\n    </ais-hits>\n  </form>\n</ais-instantsearch>\n", styles: [":host ::ng-deep .ais-SearchBox-form .ais-SearchBox-submit{display:none}:host ::ng-deep .ais-SearchBox-form .ais-SearchBox-reset{display:none}.suggestions-container.is-hidden{visibility:hidden}\n"], dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i4.NgAisInstantSearch, selector: "ais-instantsearch", inputs: ["config", "instanceName"], outputs: ["change"] }, { kind: "component", type: i4.NgAisHits, selector: "ais-hits", inputs: ["escapeHTML", "transformItems"] }, { kind: "directive", type: i1.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: i2.E11InputComponent, selector: "e11-input", inputs: ["containerClass", "inputClass", "hintText", "label", "type", "size", "style", "placeholder", "maxLength", "max", "name", "id", "iconLeft", "iconLeftBorder", "required", "hasError", "labelColor", "borderGlow", "checkIcon", "clearIcon", "autofocus", "togglePassword", "hasButton", "buttonText", "buttonIcon", "buttonIconType", "buttonIconSize", "readonly", "disabled", "disabledText", "autocomplete", "formControlName", "isRounded", "parentForm", "tooltipConfig", "icon", "iconType", "iconSize", "darkMode", "context", "enforceCharacterLimit", "showCharacterLimitWarning", "loading", "leadingIcon", "trailingIcon"], outputs: ["change", "focus", "blur"] }, { kind: "directive", type: i6.DropdownCloserDirective, selector: "[dropdownCloser]", inputs: ["dropdownCloser"], outputs: ["closeEventTriggered"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sdk-search-autocomplete', template: "<ais-instantsearch *ngIf=\"config\" #instantSearch [config]=\"config\">\n  <form class=\"e11-relative\" [formGroup]=\"formAutocomplete\" (ngSubmit)=\"onSubmitForm()\" novalidate>\n    <span #dropdownInput>\n      <e11-input\n        [type]=\"'text'\"\n        name=\"value\"\n        id=\"id_title\"\n        [borderGlow]=\"'primary'\"\n        [parentForm]=\"formAutocomplete\"\n        formControlName=\"value\"\n        autocomplete=\"off\"\n        (click)=\"openDropdown()\"\n        [label]=\"'Skills'\"\n      >\n      </e11-input>\n    </span>\n    <ais-hits>\n      <ng-template #hits let-hits=\"hits\" let-results=\"results\">\n        <div\n          [dropdownCloser]=\"dropdownInput\"\n          (closeEventTriggered)=\"dropdownHidden = true\"\n          class=\"suggestions-container e11-z-10 e11-absolute e11-bg-white e11-h-auto e11-shadow-md e11-box-border\"\n          [ngClass]=\"{ 'is-hidden': dropdownHidden }\"\n        >\n          <ul class=\"e11-p-0 e11-m-0 e11-max-h-60 e11-overflow-y-auto\">\n            <li class=\"e11-relative e11-list-none e11-p-0 e11-m-0 e11-cursor-pointer\" *ngFor=\"let hit of hits\">\n              <a\n                class=\"e11-py-3.5 e11-px-4 e11-block e11-no-underlin e11-text-skin-base e11-cursor-pointer hover:e11-bg-gray-200\"\n                [class.complete-selected]\n                (click)=\"onSelect(hit.value)\"\n                >{{ hit.value }}</a\n              >\n            </li>\n          </ul>\n        </div>\n      </ng-template>\n    </ais-hits>\n  </form>\n</ais-instantsearch>\n", styles: [":host ::ng-deep .ais-SearchBox-form .ais-SearchBox-submit{display:none}:host ::ng-deep .ais-SearchBox-form .ais-SearchBox-reset{display:none}.suggestions-container.is-hidden{visibility:hidden}\n"] }]
        }], ctorParameters: () => [{ type: AutocompleteService }, { type: i0.ElementRef }, { type: i1.FormBuilder }], propDecorators: { type: [{
                type: Input
            }], configuration: [{
                type: Input
            }], valueSelected: [{
                type: Output
            }], instantSearchInstance: [{
                type: ViewChild,
                args: ['instantSearch']
            }] } });

class SortByComponent extends TypedBaseWidget {
    constructor(parentIndex, instantSearchInstance) {
        super('SortBy');
        this.parentIndex = parentIndex;
        this.instantSearchInstance = instantSearchInstance;
        this.controls = 'buttons';
        this.items = [];
        this.isToggled = false;
        this.showOptions = false;
        this.currentSortLabel = '';
    }
    ngOnInit() {
        this.createWidget(connectSortBy, {
            items: this.items,
            transformItems: this.transformItems,
        });
        super.ngOnInit();
        this.currentSortLabel = this.items[0].label;
        this.currentSortItem = this.items[0];
    }
    click(item) {
        this.currentSortLabel = item.label;
        this.currentSortItem = item;
        const value = item.value;
        if (!item.toggleState) {
            this.state.refine(value);
            return;
        }
        if (this.state.currentRefinement != value) {
            this.isToggled = false;
            this.state.refine(value);
            return;
        }
        if (item.toggleValue) {
            const refinementValue = item.toggleValue;
            item.toggleValue = value;
            this.state.refine(refinementValue);
            this.isToggled = true;
            return;
        }
        const valueArr = value.split('_');
        const currentSortOrder = valueArr.pop();
        const newSortOrder = currentSortOrder == 'asc' ? 'desc' : 'asc';
        valueArr.push(newSortOrder);
        const reversedValue = valueArr.join('_');
        this.state.refine(reversedValue);
        this.isToggled = true;
    }
    sortByName(name) {
        const item = this.state.options.find((item) => item.name === name);
        if (!item) {
            return;
        }
        this.click(item);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SortByComponent, deps: [{ token: forwardRef(() => NgAisIndex), optional: true }, { token: forwardRef(() => NgAisInstantSearch) }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: SortByComponent, selector: "alg-sort-by", inputs: { controls: "controls", items: "items", transformItems: "transformItems" }, usesInheritance: true, ngImport: i0, template: "<!-- Buttons -->\n<span *ngIf=\"state && controls === 'buttons'\">\n  <ng-container *ngFor=\"let item of $any(state.options)\">\n    <e11-button\n      *ngIf=\"item.toggleState\"\n      (click)=\"click(item)\"\n      value=\"{{ item.label | uppercase | translate }}\"\n      color=\"primary\"\n      iconClassOverrides=\"{{ state.currentRefinement.includes(item.name) && isToggled ? 'e11-rotate-180' : '' }}\"\n      icon=\"sort\"\n      iconPosition=\"left\"\n      [iconSize]=\"'md-18'\"\n      [style]=\"state.currentRefinement.includes(item.name) ? 'pill' : 'pill outlined'\"\n      [size]=\"'sm'\"\n      [buttonGroup]=\"true\"\n    >\n    </e11-button>\n\n    <e11-button\n      *ngIf=\"!item.toggleState\"\n      (click)=\"click(item)\"\n      color=\"primary\"\n      value=\"{{ item.label | uppercase | translate }}\"\n      [style]=\"state.currentRefinement === item.value ? 'pill' : 'pill outlined'\"\n      [size]=\"'sm'\"\n      [buttonGroup]=\"true\"\n    >\n    </e11-button>\n  </ng-container>\n</span>\n\n<!-- Drop downs -->\n<div\n  (click)=\"showOptions = true\"\n  (mouseleave)=\"showOptions = false\"\n  class=\"sort-by-dropdown e11-relative e11-flex e11-text-sm e11-items-center e11-cursor-pointer e11-rounded e11-border e11-border-skin-app-borders hover:e11-border-skin-primary-accent hover:e11-text-skin-primary-accent e11-p-2\"\n  *ngIf=\"state && controls === 'dropdown'\"\n>\n  <span class=\"material-icons-outlined\"> swap_vert </span>\n  <span class=\"e11-font-primary-demibold\">{{ \"Sort by:\" | translate }} </span>\n  <span class=\"material-icons-outlined md-14 e11-ml-2 e11-mr-1\" [ngClass]=\"isToggled ? 'e11-rotate-180' : ''\"> arrow_downward </span>\n  {{ currentSortLabel }}\n  <div\n    *ngIf=\"showOptions\"\n    class=\"sort-by-dropdown-options e11-absolute e11-w-full e11-top-10 e11-left-0 e11-z-10 e11-shadow-lg e11-bg-white e11-rounded e11-border e11-border-skin-app-borders\"\n  >\n    <ng-container *ngFor=\"let item of $any(state.options)\">\n      <div\n        class=\"e11-w-full e11-flex e11-items-center e11-cursor-pointer e11-p-2\"\n        (click)=\"click(item)\"\n        [ngClass]=\"state.currentRefinement.includes(item.name) ? 'e11-bg-skin-primary-accent e11-text-white' : 'hover:e11-bg-skin-grey/20'\"\n      >\n        <span\n          class=\"material-icons-outlined md-14 e11-mr-1\"\n          [ngClass]=\"state.currentRefinement.includes(item.name) && isToggled ? 'e11-rotate-180' : ''\"\n        >\n          arrow_downward\n        </span>\n        {{ item.label }}\n      </div>\n    </ng-container>\n  </div>\n</div>\n", styles: [":host ::ng-deep .e11-button{float:left;margin-bottom:5px}:host ::ng-deep .e11-button .flip-icon{transform:rotateX(190deg)}\n"], dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2$1.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }, { kind: "pipe", type: i1$1.UpperCasePipe, name: "uppercase" }, { kind: "pipe", type: i3.TranslatePipe, name: "translate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SortByComponent, decorators: [{
            type: Component,
            args: [{ selector: 'alg-sort-by', template: "<!-- Buttons -->\n<span *ngIf=\"state && controls === 'buttons'\">\n  <ng-container *ngFor=\"let item of $any(state.options)\">\n    <e11-button\n      *ngIf=\"item.toggleState\"\n      (click)=\"click(item)\"\n      value=\"{{ item.label | uppercase | translate }}\"\n      color=\"primary\"\n      iconClassOverrides=\"{{ state.currentRefinement.includes(item.name) && isToggled ? 'e11-rotate-180' : '' }}\"\n      icon=\"sort\"\n      iconPosition=\"left\"\n      [iconSize]=\"'md-18'\"\n      [style]=\"state.currentRefinement.includes(item.name) ? 'pill' : 'pill outlined'\"\n      [size]=\"'sm'\"\n      [buttonGroup]=\"true\"\n    >\n    </e11-button>\n\n    <e11-button\n      *ngIf=\"!item.toggleState\"\n      (click)=\"click(item)\"\n      color=\"primary\"\n      value=\"{{ item.label | uppercase | translate }}\"\n      [style]=\"state.currentRefinement === item.value ? 'pill' : 'pill outlined'\"\n      [size]=\"'sm'\"\n      [buttonGroup]=\"true\"\n    >\n    </e11-button>\n  </ng-container>\n</span>\n\n<!-- Drop downs -->\n<div\n  (click)=\"showOptions = true\"\n  (mouseleave)=\"showOptions = false\"\n  class=\"sort-by-dropdown e11-relative e11-flex e11-text-sm e11-items-center e11-cursor-pointer e11-rounded e11-border e11-border-skin-app-borders hover:e11-border-skin-primary-accent hover:e11-text-skin-primary-accent e11-p-2\"\n  *ngIf=\"state && controls === 'dropdown'\"\n>\n  <span class=\"material-icons-outlined\"> swap_vert </span>\n  <span class=\"e11-font-primary-demibold\">{{ \"Sort by:\" | translate }} </span>\n  <span class=\"material-icons-outlined md-14 e11-ml-2 e11-mr-1\" [ngClass]=\"isToggled ? 'e11-rotate-180' : ''\"> arrow_downward </span>\n  {{ currentSortLabel }}\n  <div\n    *ngIf=\"showOptions\"\n    class=\"sort-by-dropdown-options e11-absolute e11-w-full e11-top-10 e11-left-0 e11-z-10 e11-shadow-lg e11-bg-white e11-rounded e11-border e11-border-skin-app-borders\"\n  >\n    <ng-container *ngFor=\"let item of $any(state.options)\">\n      <div\n        class=\"e11-w-full e11-flex e11-items-center e11-cursor-pointer e11-p-2\"\n        (click)=\"click(item)\"\n        [ngClass]=\"state.currentRefinement.includes(item.name) ? 'e11-bg-skin-primary-accent e11-text-white' : 'hover:e11-bg-skin-grey/20'\"\n      >\n        <span\n          class=\"material-icons-outlined md-14 e11-mr-1\"\n          [ngClass]=\"state.currentRefinement.includes(item.name) && isToggled ? 'e11-rotate-180' : ''\"\n        >\n          arrow_downward\n        </span>\n        {{ item.label }}\n      </div>\n    </ng-container>\n  </div>\n</div>\n", styles: [":host ::ng-deep .e11-button{float:left;margin-bottom:5px}:host ::ng-deep .e11-button .flip-icon{transform:rotateX(190deg)}\n"] }]
        }], ctorParameters: () => [{ type: i4.NgAisIndex, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => NgAisIndex)]
                }, {
                    type: Optional
                }] }, { type: i4.NgAisInstantSearch, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => NgAisInstantSearch)]
                }] }], propDecorators: { controls: [{
                type: Input
            }], items: [{
                type: Input
            }], transformItems: [{
                type: Input
            }] } });

const connectRefresh = (renderFn, unmountFn) => (widgetParams) => ({
    $$type: 'custom.refresh',
    init: function ({ instantSearchInstance }) {
        renderFn({
            refresh() { },
            widgetParams,
            instantSearchInstance,
        }, true);
    },
    render({ instantSearchInstance }) {
        const refresh = instantSearchInstance.refresh.bind(instantSearchInstance);
        renderFn({ refresh, widgetParams, instantSearchInstance }, false);
    },
    dispose() {
        if (unmountFn) {
            unmountFn();
        }
    },
});
class RefreshComponent extends TypedBaseWidget {
    // Rendering options
    constructor(parentIndex, instantSearchInstance, logger) {
        super('Refresh');
        this.parentIndex = parentIndex;
        this.instantSearchInstance = instantSearchInstance;
        this.logger = logger;
        this.state = {
            refresh() { },
        };
    }
    ngOnInit() {
        this.createWidget(connectRefresh, {
        // instance options
        });
        super.ngOnInit();
    }
    async refresh(options) {
        if (options?.delayMs)
            await sleep(options.delayMs);
        this.logger.log(`RefreshComponent - refreshing after ${options?.delayMs} ms`);
        this.state.refresh();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: RefreshComponent, deps: [{ token: forwardRef(() => NgAisIndex), optional: true }, { token: forwardRef(() => NgAisInstantSearch) }, { token: i1$2.E11Logger }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.9", type: RefreshComponent, selector: "search-refresh", usesInheritance: true, ngImport: i0, template: `<!--<button (click)="state.refresh()">Refresh</button>--> `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: RefreshComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'search-refresh',
                    template: `<!--<button (click)="state.refresh()">Refresh</button>--> `,
                }]
        }], ctorParameters: () => [{ type: i4.NgAisIndex, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => NgAisIndex)]
                }, {
                    type: Optional
                }] }, { type: i4.NgAisInstantSearch, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => NgAisInstantSearch)]
                }] }, { type: i1$2.E11Logger }] });

const DECLARE_EXPORTS = [AutocompleteComponent, SearchFormComponent, SortByComponent, RefreshComponent];
class SearchModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SearchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: SearchModule, declarations: [AutocompleteComponent, SearchFormComponent, SortByComponent, RefreshComponent], imports: [CommonModule, NgAisModule, FormsModule, ReactiveFormsModule, E11InputModule, WebUtilityModule, TranslateModule, E11ButtonModule], exports: [AutocompleteComponent, SearchFormComponent, SortByComponent, RefreshComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SearchModule, imports: [CommonModule, NgAisModule, FormsModule, ReactiveFormsModule, E11InputModule, WebUtilityModule, TranslateModule, E11ButtonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: SearchModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...DECLARE_EXPORTS],
                    imports: [CommonModule, NgAisModule, FormsModule, ReactiveFormsModule, E11InputModule, WebUtilityModule, TranslateModule, E11ButtonModule],
                    exports: [...DECLARE_EXPORTS],
                }]
        }] });

class SearchEngine {
    constructor(options) {
        this.miniSearch = new MiniSearch(options);
    }
    addAll(documents) {
        this.miniSearch.addAll(documents);
        this.allDocuments = documents;
    }
    search(queryText) {
        const results = this.miniSearch.search(queryText);
        if (results.length < 1) {
            return [];
        }
        else {
            const ids = results.map((r) => r.id);
            return this.allDocuments?.filter((n) => ids.includes(n.id)) || [];
        }
    }
}

/*
 * Public API Surface of search
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AutoCompleteRepository, AutocompleteComponent, AutocompleteService, COLLECTION_AUTOCOMPLETE, RefreshComponent, SearchEngine, SearchFormComponent, SearchModule, SortByComponent };
